import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { router } from '@inertiajs/react'
import type { GoogleAccountOption } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  workerId: string
  googleAdsAccounts: GoogleAccountOption[]
  googleAnalyticsProperties: GoogleAccountOption[]
}

export default function GoogleAccountsModal({ open, onOpenChange, workerId, googleAdsAccounts, googleAnalyticsProperties }: Props) {
  const [selectedAdsIds, setSelectedAdsIds] = useState<string[]>(
    googleAdsAccounts.filter((a) => a.selected).map((a) => a.id)
  )
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(
    googleAnalyticsProperties.filter((p) => p.selected).map((p) => p.id)
  )
  const [saving, setSaving] = useState(false)

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setSelectedAdsIds(googleAdsAccounts.filter((a) => a.selected).map((a) => a.id))
      setSelectedPropertyIds(googleAnalyticsProperties.filter((p) => p.selected).map((p) => p.id))
    }
    onOpenChange(isOpen)
  }

  const toggleAds = (id: string) => setSelectedAdsIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  const toggleProperty = (id: string) => setSelectedPropertyIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])

  const handleSave = () => {
    setSaving(true)
    router.post(`/dashboard/digital_workers/${workerId}/update_google_accounts`, {
      google_ads_account_ids: selectedAdsIds,
      google_analytics_property_ids: selectedPropertyIds,
    }, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setSaving(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Selecionar Contas Google</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-foreground mb-3">Google Ads</p>
            {googleAdsAccounts.length > 0 ? (
              <div className="space-y-2">
                {googleAdsAccounts.map((account) => (
                  <div key={account.id} className="flex items-center gap-2">
                    <Checkbox id={`ads-${account.id}`} checked={selectedAdsIds.includes(account.id)} onCheckedChange={() => toggleAds(account.id)} />
                    <Label htmlFor={`ads-${account.id}`} className="text-sm font-normal">{account.name} ({account.external_id.split('/').pop()})</Label>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhuma conta Google Ads encontrada.{' '}
                <Link href="/dashboard/integrations/google" className="text-foreground hover:underline">Configurar integração Google</Link>
              </p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-3">Google Analytics</p>
            {googleAnalyticsProperties.length > 0 ? (
              <div className="space-y-2">
                {googleAnalyticsProperties.map((property) => (
                  <div key={property.id} className="flex items-center gap-2">
                    <Checkbox id={`prop-${property.id}`} checked={selectedPropertyIds.includes(property.id)} onCheckedChange={() => toggleProperty(property.id)} />
                    <Label htmlFor={`prop-${property.id}`} className="text-sm font-normal">{property.name} ({property.external_id.split('/').pop()})</Label>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhuma propriedade Google Analytics encontrada.{' '}
                <Link href="/dashboard/integrations/google" className="text-foreground hover:underline">Configurar integração Google</Link>
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
